import React, { useState, useEffect, useCallback } from 'react';
import Grid from '@mui/material/Grid2';
import {  
  Feature,
} from "@models";
import {  
  Summon,
  DamageMultiplier,
  Skill,
  Tool,
  Condition,
  UpgradableNumber,
  TemplateBase, 
  Creature,
  CreatureAbility,
  MinionAbility,
  AbilityScoresUpgradable,
  Sense,
  SENSES,
  SKILLS,
} from "@dndModels";
import {
  CREATURE_TYPES,
  DAMAGE_TYPES,
  SIZE_MAP
} from "@dndModels/Constants";

import API from "@dndUtilities/system_api";
import CommonUtilities from "@utilities/common_utilities";

import StringBox from "@components/input/strings/StringBox";
import ToggleButtonBox from "@components/input/buttons/ToggleButtonBox";
import SelectStringBox from "@components/input/selects/SelectStringBox";
import LoadingBox from "@components/input/LoadingBox";

import UpgradableNumberBox from "@dndComponents/UpgradableNumberBox";

import CreatureAbilityScoresUpgradableInput from "@dndComponents/creature/CreatureAbilityScoresUpgradableInput";
import FeatureListInput from "@dndComponents/feature/FeatureListInput";
import FeatureInput from "@dndComponents/feature/FeatureInput";

import TemplateBox from "@dndComponents/TemplateBox";
import CenteredMenu from "@components/input/menus/CenteredMenu";
import StringsBox from "@components/input/strings/StringsBox";
import { CreatureType } from '@dndModels/Types';
import CheckBox from '@components/input/CheckBox';

interface Props { 
  obj: Summon;
  onChange: (changed: Summon) => void;
  slot_level: number;
  disabled: boolean;
}

const api = API.getInstance();
const common_util = CommonUtilities.getInstance();

export default function SummonStatBlockInput({ 
  obj,
  onChange,
  slot_level,
  disabled
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [tools, setTools] = useState<Tool[]>([]);
  const [conditions, setConditions] = useState<Condition[]>([]);
  const [senses, setSenses] = useState<Sense[]>([]);
  const [mode, setMode] = useState("Home");
  const [ping, setPing] = useState(false);
  const [expanded_feature, setExpandedFeature] = useState<Feature | null>(null);
  const [edit_attribute, setEditAttribute] = useState("");

  const load = useCallback( async () => {
    const res = await api.getSetOfObjects({ data_types: ["tool","condition"]});
    setTools(res.tool as Tool[]);
    setConditions(res.condition as Condition[]);
    const new_senses = SENSES;
    setSenses(new_senses);
    const my_senses = Object.keys(obj.senses);
    if (!common_util.list1ContainsList2(new_senses.map(o => o.name), my_senses)) {
      const new_obj = obj;
      for (const sense of my_senses) {
        if (new_senses.filter(o => o.name === sense).length === 0) {
          delete new_obj.senses[sense];
        }
      }
      onChange(new_obj);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      setLoading(true);
      setTimeout(() => {
        load();
      }, 100);
    }
  }, [fetched, load]);

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const copyCreature = async (copyMe: Creature) => {
    const new_obj = obj;
    new_obj.name = copyMe.name;
    new_obj.description = copyMe.description;
    new_obj.creature_type = copyMe.creature_type;
    new_obj.swarm = copyMe.swarm;
    new_obj.subtypes = [...copyMe.subtypes];
    new_obj.challenge_rating = new UpgradableNumber();
    new_obj.challenge_rating.base = copyMe.challenge_rating;
    new_obj.image_url = copyMe.image_url;
    new_obj.ability_scores = copyMe.ability_scores.clone();
    new_obj.actions = [];
    for (const fb of copyMe.action_fbs) {
      for (const f of fb.features) {
        if (f.floating_feature instanceof CreatureAbility) {
          const clone = f.clone(true);
          const ff_finder = api.all_floating_features.filter(o2 => o2 instanceof MinionAbility && o2.name === f.name && o2.description === f.description);
          if (ff_finder.length === 1) {
            const floating_feature = ff_finder[0];
            clone.floating_feature_id = floating_feature._id;
            clone.floating_feature = floating_feature;
          } else {
            const floating_feature = new MinionAbility();
            floating_feature.copyCreatureAbility(f.floating_feature);
            const res = await api.upsertObject(floating_feature.data_type, floating_feature);
            if (res) {
              floating_feature._id = res.body.id;
              api.add_floating_feature(floating_feature);
              clone.floating_feature_id = floating_feature._id;
              clone.floating_feature = floating_feature;
            }
          }
          new_obj.actions.push(clone);
        } else {
          new_obj.actions.push(f.clone(true));
        }
      }
    }
    new_obj.legendary_actions = [];
    for (const fb of copyMe.legendary_action_fbs) {
      for (const f of fb.features) {
        if (f.floating_feature instanceof CreatureAbility) {
          const clone = f.clone(true);
          const ff_finder = api.all_floating_features.filter(o2 => o2 instanceof MinionAbility && o2.name === f.name && o2.description === f.description);
          if (ff_finder.length === 1) {
            const floating_feature = ff_finder[0];
            clone.floating_feature_id = floating_feature._id;
            clone.floating_feature = floating_feature;
          } else {
            const floating_feature = new MinionAbility();
            floating_feature.copyCreatureAbility(f.floating_feature);
            const res = await api.upsertObject(floating_feature.data_type, floating_feature);
            if (res) {
              floating_feature._id = res.body.id;
              api.add_floating_feature(floating_feature);
              clone.floating_feature_id = floating_feature._id;
              clone.floating_feature = floating_feature;
            }
          }
          new_obj.legendary_actions.push(clone);
        } else {
          new_obj.legendary_actions.push(f.clone(true));
        }
      }
    }
    new_obj.special_abilities = [];
    for (const fb of copyMe.traits) {
      for (const f of fb.features) {
        if (f.floating_feature instanceof CreatureAbility) {
          const clone = f.clone(true);
          const ff_finder = api.all_floating_features.filter(o2 => o2 instanceof MinionAbility && o2.name === f.name && o2.description === f.description);
          if (ff_finder.length === 1) {
            const floating_feature = ff_finder[0];
            clone.floating_feature_id = floating_feature._id;
            clone.floating_feature = floating_feature;
          } else {
            const floating_feature = new MinionAbility();
            floating_feature.copyCreatureAbility(f.floating_feature);
            const res = await api.upsertObject(floating_feature.data_type, floating_feature);
            if (res) {
              floating_feature._id = res.body.id;
              api.add_floating_feature(floating_feature);
              clone.floating_feature_id = floating_feature._id;
              clone.floating_feature = floating_feature;
            }
          }
          new_obj.special_abilities.push(clone);
        } else {
          new_obj.special_abilities.push(f.clone(true));
        }
      }
    }
    new_obj.hit_dice_size = new UpgradableNumber();
    new_obj.hit_dice_count = new UpgradableNumber();
    if (copyMe.hit_dice.length > 0) {
      new_obj.hit_dice_size.base = copyMe.hit_dice[0].size;
      new_obj.hit_dice_count = copyMe.hit_dice[0].count.clone();
    }
    new_obj.max_hit_points = new UpgradableNumber();
    const attributes = copyMe.attributes_table.getEntry(copyMe.challenge_rating);
    if (attributes) {
      new_obj.speed = { ...attributes.speeds };
      new_obj.saving_throws = new AbilityScoresUpgradable(); 
      // attributes.save_profs.clone();
      new_obj.languages = attributes.languages;
      new_obj.tool_proficiencies = [ ...attributes.tool_profs ];
      // obj.skill_proficiencies = { ...copyMe.skill_proficiencies };
      new_obj.skill_proficiencies_upgradable = [];
      const skill_ids = Object.keys(attributes.skill_profs);
      for (const _id of skill_ids) {
        const skill = SKILLS.find(o => o._id === _id);
        if (skill) {
          new_obj.skill_proficiencies_upgradable.push({ _id, name: skill.name, value: new UpgradableNumber(attributes.skill_profs[_id]) });
        }
      }
      new_obj.condition_immunities = [...attributes.condition_immunities];
      // obj.damage_multipliers = [];
      // copyMe.damage_multipliers.forEach(dm => {
      //   obj.damage_multipliers.push(dm.clone());
      // });
      new_obj.senses = { ...attributes.senses };
    }
    new_obj.initiative_modifier = copyMe.initiative_modifier.clone();
    new_obj.armor_class = copyMe.armor_class.clone();
    new_obj.size = SIZE_MAP[copyMe.size_num_for_cr(copyMe.challenge_rating)];
    new_obj.alignment = copyMe.alignment;
    new_obj.connected = false;
    return new_obj;
  }

  const render_tab = () => {
    if (mode === "Ability Scores") {
      return (
        <CreatureAbilityScoresUpgradableInput
          obj={obj}
          disabled={disabled}
          onChange={(changed: AbilityScoresUpgradable) => {
            const new_obj = obj;
            new_obj.ability_scores = changed;
            onChange(new_obj);
            setPing(!ping);
          }}
        />
      );
    } else if (mode === "Attributes") {
      return render_attributes();
    } else if (mode === "Actions") {
      if (expanded_feature) {
        return (
          <FeatureInput
            label="Action"
            slot_level={slot_level}
            parent_name={obj.name}
            base_name={null}
            minion
            feature={expanded_feature} 
            disabled={disabled}
            onChange={(changed: Feature) => {
              const new_obj = obj;
              new_obj.actions = common_util.replaceInList(obj.actions, changed.true_id, changed, "true_id");
              onChange(new_obj);
              setPing(!ping);
            }}
            onDelete={() => {
              if (expanded_feature) {
                const id = expanded_feature.true_id;
                const new_obj = obj;
                const features = new_obj.actions.filter(o => o.true_id !== id);
                new_obj.actions = features;
                setExpandedFeature(null);
                onChange(new_obj);
                setPing(!ping);
              }
            }}
            onDone={() => {
              setExpandedFeature(null);
            }}
          />
        );
      } else {
        return (
          <FeatureListInput 
            label="Action"
            features={obj.actions} 
            parent_id={obj.true_id} 
            parent_type="SummonStatBlock"
            disabled={disabled}
            onExpand={setExpandedFeature}
            onAdd={() => {
              const new_obj = obj;
              const feature = new Feature();
              // feature.parent_type = "SummonStatBlock";
              // feature.parent_id = obj.true_id;
              // feature.id = obj.actions.length;
              new_obj.actions.push(feature);
              setExpandedFeature(feature);
              onChange(new_obj);
              setPing(!ping);
            }}
          />
        );
      }
    } else if (mode === "Legendary Actions") {
      if (expanded_feature) {
        return (
          <FeatureInput
            label="Legendary Action"
            slot_level={slot_level}
            parent_name={obj.name}
            base_name={null}
            minion
            feature={expanded_feature} 
            disabled={disabled}
            onChange={(changed: Feature) => {
              const new_obj = obj;
              new_obj.legendary_actions = common_util.replaceInList(new_obj.legendary_actions, changed.true_id, changed, "true_id");
              
              onChange(new_obj);
              setPing(!ping);
            }}
            onDelete={() => {
              if (expanded_feature) {
                const id = expanded_feature.true_id;
                const new_obj = obj;
                const features = new_obj.legendary_actions.filter(o => o.true_id !== id);
                new_obj.legendary_actions = features;
                
                setExpandedFeature(null);
                onChange(new_obj);
                setPing(!ping);
              }
            }}
            onDone={() => {
              setExpandedFeature(null);
            }}
          />
        );
      } else {
        return (
          <FeatureListInput 
            label="Legendary Action"
            features={obj.legendary_actions} 
            parent_id={obj.true_id} 
            parent_type="SummonStatBlock"
            disabled={disabled}
            onExpand={setExpandedFeature}
            onAdd={() => {
              const new_obj = obj;
              const feature = new Feature();
              // feature.parent_type = "SummonStatBlock";
              // feature.parent_id = obj.true_id;
              new_obj.legendary_actions.push(feature);
              setExpandedFeature(null);
              onChange(new_obj);
              setPing(!ping);
            }}
          />
        );
      }
    } else if (mode === "Special Abilities") {
      if (expanded_feature) {
        return (
          <FeatureInput
            label="Special Ability"
            slot_level={slot_level}
            parent_name={obj.name}
            base_name={null}
            minion
            feature={expanded_feature} 
            disabled={disabled}
            onChange={(changed: Feature) => {
              const new_obj = obj;
              new_obj.special_abilities = common_util.replaceInList(new_obj.special_abilities, changed.true_id, changed, "true_id");
              onChange(new_obj);
              setPing(!ping);
            }}
            onDelete={() => {
              if (expanded_feature) {
                const id = expanded_feature.true_id;
                const new_obj = obj;
                const features = new_obj.special_abilities.filter(o => o.true_id !== id);
                new_obj.special_abilities = features;
                setExpandedFeature(null);
                onChange(new_obj);
                setPing(!ping);
              }
            }}
            onDone={() => {
              setExpandedFeature(null);
            }}
          />
        );
      } else {
        return (
          <FeatureListInput 
            label="Special Ability"
            plural="Special Abilities"
            features={obj.special_abilities} 
            parent_id={obj.true_id} 
            parent_type="SummonStatBlock"
            disabled={disabled}
            onExpand={setExpandedFeature}
            onAdd={() => {
              const new_obj = obj;
              const feature = new Feature();
              // feature.parent_type = "SummonStatBlock";
              // feature.parent_id = obj.true_id;
              new_obj.special_abilities.push(feature);
              setExpandedFeature(feature);
              onChange(new_obj);
              setPing(!ping);
            }}
          />
        );
      }
    } else {
      return (
        <Grid container spacing={0} direction="row">
          <Grid
            container
            spacing={0}
            direction="column"
            size={{
              xs: 8
            }}>
            <Grid>
              <StringBox  
                name="Name"
                value={obj.name} 
                message={obj.name.length > 0 ? "" : "Name Invalid"}
                disabled={disabled}
                onBlur={(changed: string) => {
                  const new_obj = obj;
                  new_obj.name = changed;
                  onChange(new_obj);
                  setPing(!ping);
                }}
              />
            </Grid>
            <Grid>
              <StringBox 
                name="Description"
                value={obj.description} 
                multiline
                disabled={disabled}
                onBlur={(changed: string) => {
                  const new_obj = obj;
                  new_obj.description = changed;
                  onChange(new_obj);
                  setPing(!ping);
                }}
              />
            </Grid>
            <Grid container spacing={0} direction="row">
              <Grid size={4}>
                <SelectStringBox 
                  name="Creature Type" 
                  options={CREATURE_TYPES}
                  value={obj.creature_type} 
                  disabled={disabled}
                  onChange={(changed: CreatureType) => {
                    const new_obj = obj;
                    new_obj.creature_type = changed;
                    onChange(new_obj);
                    setPing(!ping);
                  }} 
                />
              </Grid>
              <Grid size={4}>
                <StringsBox 
                  name="Subtype"
                  values={obj.subtypes} 
                  disabled={disabled}
                  onChange={(changed: string[]) => {
                    const new_obj = obj;
                    new_obj.subtypes = changed;
                    onChange(new_obj);
                    setPing(!ping);
                  }}
                />
              </Grid>
              <Grid size={4}>
                <CheckBox 
                  name="Swarm"
                  value={obj.swarm} 
                  disabled={disabled}
                  onChange={(changed: boolean) => {
                    const new_obj = obj;
                    new_obj.swarm = changed;
                    onChange(new_obj);
                    setPing(!ping);
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid
            container
            spacing={0}
            direction="row"
            size={{
              xs: 4
            }}>
            <Grid
              size={{
                xs: 6
              }}>
              <UpgradableNumberBox
                name="Hit Dice Count"
                slot_level={slot_level}
                value={obj.hit_dice_count} 
                disabled={disabled}
                onChange={(changed: UpgradableNumber) => {
                  const new_obj = obj;
                  new_obj.hit_dice_count = changed;
                  onChange(new_obj);
                  setPing(!ping);
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 6
              }}>
              <UpgradableNumberBox
                name="Hit Dice Size"
                slot_level={slot_level}
                value={obj.hit_dice_size} 
                disabled={disabled}
                onChange={(changed: UpgradableNumber) => {
                  const new_obj = obj;
                  new_obj.hit_dice_size = changed;
                  onChange(new_obj);
                  setPing(!ping);
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 6
              }}>
              <UpgradableNumberBox
                name="Max Hit Points"
                slot_level={slot_level}
                value={obj.max_hit_points} 
                disabled={disabled}
                onChange={(changed: UpgradableNumber) => {
                  const new_obj = obj;
                  new_obj.max_hit_points = changed;
                  onChange(new_obj);
                  setPing(!ping);
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 6
              }}>
              <UpgradableNumberBox
                name="Temp Hit Points"
                slot_level={slot_level}
                value={obj.temp_hit_points} 
                disabled={disabled}
                onChange={(changed: UpgradableNumber) => {
                  const new_obj = obj;
                  new_obj.temp_hit_points = changed;
                  onChange(new_obj);
                  setPing(!ping);
                }}
              />
            </Grid>
          </Grid>
          <Grid
            size={{
              xs: 4
            }}>
            <UpgradableNumberBox
              name="Challenge Rating"
              slot_level={slot_level}
              value={obj.challenge_rating} 
              disabled={disabled}
              onChange={(changed: UpgradableNumber) => {
                const new_obj = obj;
                new_obj.challenge_rating = changed;
                onChange(new_obj);
                setPing(!ping);
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 4
            }}>
            <UpgradableNumberBox
              name="Initiative Modifier"
              slot_level={slot_level}
              value={obj.initiative_modifier} 
              disabled={disabled}
              onChange={(changed: UpgradableNumber) => {
                const new_obj = obj;
                new_obj.initiative_modifier = changed;
                onChange(new_obj);
                setPing(!ping);
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 4
            }}>
            <UpgradableNumberBox
              name="Armor Class"
              slot_level={slot_level}
              value={obj.armor_class} 
              disabled={disabled}
              onChange={(changed: UpgradableNumber) => {
                const new_obj = obj;
                new_obj.armor_class = changed;
                onChange(new_obj);
                setPing(!ping);
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 4
            }}>
            <SelectStringBox 
              name="Size"
              options={["Tiny","Small","Medium","Large","Huge","Gargantuan"]}
              value={obj.size} 
              disabled={disabled}
              onChange={(changed: string) => {
                const new_obj = obj;
                new_obj.size = changed;
                onChange(new_obj);
                setPing(!ping);
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 4
            }}>
            <SelectStringBox  
              name="Alignment"
              options={[
                "Lawful Good",
                "Lawful Neutral",
                "Lawful Evil",
                "Neutral Good",
                "Neutral",
                "Neutral Evil",
                "Chaotic Good",
                "Chaotic Neutral",
                "Chaotic Evil",
                "Any Alignment",
                "Any Chaotic Alignment",
                "Any Evil Alignment",
                "Any Non-good Alignment",
                "Any Non-lawful Alignment",
                "Unaligned",
                "Neutral Good (50%) Or Neutral Evil (50%)"
              ]}
              value={obj.alignment}
              disabled={disabled}
              onChange={(changed: string) => {
                const new_obj = obj;
                new_obj.alignment = changed;
                onChange(new_obj);
                setPing(!ping);
              }}
            />
          </Grid>
        </Grid>
      ); 
    }
  }

  const render_attributes = () => {
    if (edit_attribute === "speed") {
      return (
        <Grid container spacing={0} direction="row">
          <Grid
            style={{ cursor: "pointer", fontWeight: "bold" }}
            onClick={() => {
              setEditAttribute("");
            }}
            size={{
              xs: 2
            }}>
            Back
          </Grid>
          <Grid
            size={{
              xs: 10
            }}>
            Speed
          </Grid>
          { renderSpeeds() }
        </Grid>
      );
    } else if (edit_attribute === "senses") {
      return (
        <Grid container spacing={0} direction="row">
          <Grid
            style={{ cursor: "pointer", fontWeight: "bold" }}
            onClick={() => {
              setEditAttribute("");
            }}
            size={{
              xs: 2
            }}>
            Back
          </Grid>
          <Grid
            size={{
              xs: 10
            }}>
            Senses
          </Grid>
          { renderSenses() }
        </Grid>
      );
    } else if (edit_attribute === "condition_immunities") {
      return (
        <Grid container spacing={0} direction="row">
          <Grid
            style={{ cursor: "pointer", fontWeight: "bold" }}
            onClick={() => {
              setEditAttribute("");
            }}
            size={{
              xs: 2
            }}>
            Back
          </Grid>
          <Grid
            size={{
              xs: 10
            }}>
            Condition Immunities
          </Grid>
          { conditions.filter(o => o.common).sort((a,b) => a.name.localeCompare(b.name)).map((condition, key) => {
            return renderCondition(condition, key);
          })}
          {/* { conditions.filter(o => !o.common).sort((a,b) => a.name.localeCompare(b.name)).map((condition, key) => {
            return renderCondition(condition, key);
          })} */}
        </Grid>
      );
    } else if (edit_attribute === "immunities") {
      return (
        <Grid container spacing={0} direction="row">
          <Grid
            style={{ cursor: "pointer", fontWeight: "bold" }}
            onClick={() => {
              setEditAttribute("");
            }}
            size={{
              xs: 2
            }}>
            Back
          </Grid>
          <Grid
            size={{
              xs: 10
            }}>
            Damage Immunities
          </Grid>
          { DAMAGE_TYPES.map((damage_type, key) => {
            return renderDamageType(damage_type, key, 0);
          })}
        </Grid>
      );
    } else if (edit_attribute === "resistances") {
      return (
        <Grid container spacing={0} direction="row">
          <Grid
            style={{ cursor: "pointer", fontWeight: "bold" }}
            onClick={() => {
              setEditAttribute("");
            }}
            size={{
              xs: 2
            }}>
            Back
          </Grid>
          <Grid
            size={{
              xs: 10
            }}>
            Damage Resistances
          </Grid>
          { DAMAGE_TYPES.map((damage_type, key) => {
            return renderDamageType(damage_type, key, 0.5);
          })}
        </Grid>
      );
    } else if (edit_attribute === "vulnerabilities") {
      return (
        <Grid container spacing={0} direction="row">
          <Grid
            style={{ cursor: "pointer", fontWeight: "bold" }}
            onClick={() => {
              setEditAttribute("");
            }}
            size={{
              xs: 2
            }}>
            Back
          </Grid>
          <Grid
            size={{
              xs: 10
            }}>
            Damage Vulnerabilities
          </Grid>
          { DAMAGE_TYPES.map((damage_type, key) => {
            return renderDamageType(damage_type, key, 2);
          })}
        </Grid>
      );
    } else if (edit_attribute === "tools") {
      return (
        <Grid container spacing={0} direction="row">
          <Grid
            style={{ cursor: "pointer", fontWeight: "bold" }}
            onClick={() => {
              setEditAttribute("");
            }}
            size={{
              xs: 2
            }}>
            Back
          </Grid>
          <Grid
            size={{
              xs: 10
            }}>
            Tools
          </Grid>
          { tools && tools.map((tool, key) => {
            return renderTool(tool, key);
          })}
        </Grid>
      );
    } else if (edit_attribute === "skills") {
      return (
        <Grid container spacing={0} direction="row">
          <Grid
            style={{ cursor: "pointer", fontWeight: "bold" }}
            onClick={() => {
              setEditAttribute("");
            }}
            size={{
              xs: 2
            }}>
            Back
          </Grid>
          <Grid
            size={{
              xs: 10
            }}>
            Skills
          </Grid>
          { SKILLS.map(skill => {
            return renderSkill(skill);
          })}
        </Grid>
      );
    } else if (edit_attribute === "saving_throws") {
      return (
        <Grid container spacing={0} direction="row">
          <Grid
            style={{ cursor: "pointer", fontWeight: "bold" }}
            onClick={() => {
              setEditAttribute("");
            }}
            size={{
              xs: 2
            }}>
            Back
          </Grid>
          <Grid
            size={{
              xs: 10
            }}>
            Saving Throws
          </Grid>
          <Grid
            size={{
              xs: 4
            }}>
            <UpgradableNumberBox
              name="STR"
              slot_level={slot_level}
              value={ obj.saving_throws.STR } 
              disabled={disabled}
              onChange={(changed: UpgradableNumber) => {
                const new_obj = obj;
                new_obj.saving_throws.STR.copy(changed);
                onChange(new_obj);
                setPing(!ping);
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 4
            }}>
            <UpgradableNumberBox
              name="DEX"
              slot_level={slot_level}
              value={ obj.saving_throws.DEX } 
              disabled={disabled}
              onChange={(changed: UpgradableNumber) => {
                const new_obj = obj;
                new_obj.saving_throws.DEX.copy(changed);
                onChange(new_obj);
                setPing(!ping);
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 4
            }}>
            <UpgradableNumberBox
              name="CON"
              slot_level={slot_level}
              value={ obj.saving_throws.CON } 
              disabled={disabled}
              onChange={(changed: UpgradableNumber) => {
                const new_obj = obj;
                new_obj.saving_throws.CON.copy(changed);
                onChange(new_obj);
                setPing(!ping);
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 4
            }}>
            <UpgradableNumberBox
              name="INT"
              slot_level={slot_level}
              value={ obj.saving_throws.INT } 
              disabled={disabled}
              onChange={(changed: UpgradableNumber) => {
                const new_obj = obj;
                new_obj.saving_throws.INT.copy(changed);
                onChange(new_obj);
                setPing(!ping);
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 4
            }}>
            <UpgradableNumberBox
              name="WIS"
              slot_level={slot_level}
              value={ obj.saving_throws.WIS } 
              disabled={disabled}
              onChange={(changed: UpgradableNumber) => {
                const new_obj = obj;
                new_obj.saving_throws.WIS.copy(changed);
                onChange(new_obj);
                setPing(!ping);
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 4
            }}>
            <UpgradableNumberBox
              name="CHA"
              slot_level={slot_level}
              value={ obj.saving_throws.CHA } 
              disabled={disabled}
              onChange={(changed: UpgradableNumber) => {
                const new_obj = obj;
                new_obj.saving_throws.CHA.copy(changed);
                onChange(new_obj);
                setPing(!ping);
              }}
            />
          </Grid>
        </Grid>
      );
    } else if (edit_attribute === "languages") {
      return (
        <Grid container spacing={0} direction="row">
          <Grid
            style={{ cursor: "pointer", fontWeight: "bold" }}
            onClick={() => {
              setEditAttribute("");
            }}
            size={{
              xs: 2
            }}>
            Back
          </Grid>
          <Grid
            size={{
              xs: 10
            }}>
            Languages
          </Grid>
          <Grid
            size={{
              xs: 12
            }}>
            <StringBox
              name="Languages"
              value={obj.languages}
              disabled={disabled}
              onBlur={(changed: string) => {
                const new_obj = obj;
                new_obj.languages = changed;
                onChange(new_obj);
                setPing(!ping);
              }}
            />
          </Grid>
        </Grid>
      );
    } else {
      return (
        <Grid container spacing={0} direction="column" style={{ cursor: "pointer" }}>
          <Grid
            onClick={() => {
              setEditAttribute("speed");
            }}>
            <b>Speed</b> { obj.speed_string }
          </Grid>
          <Grid
            onClick={() => {
              setEditAttribute("senses");
            }}>
            <b>Senses</b> { obj.senses_string }
          </Grid>
          <Grid
            onClick={() => {
              setEditAttribute("immunities");
            }}>
            <b>Damage Immunities</b> { obj.immunities_string }
          </Grid>
          <Grid
            onClick={() => {
              setEditAttribute("resistances");
            }}>
            <b>Damage Resistances</b> { obj.resistances_string }
          </Grid>
          <Grid
            onClick={() => {
              setEditAttribute("vulnerabilities");
            }}>
            <b>Damage Vulnerabilities</b> { obj.vulnerabilities_string }
          </Grid>
          <Grid
            onClick={() => {
              setEditAttribute("condition_immunities");
            }}>
            <b>Condition Immunities</b> { obj.condition_immunities_string }
          </Grid>
          <Grid
            onClick={() => {
              setEditAttribute("tools");
            }}>
            <b>Tools</b> { obj.tools_string }
          </Grid>
          <Grid
            onClick={() => {
              setEditAttribute("skills");
            }}>
            <b>Skills</b> { obj.skills_string }
          </Grid>
          <Grid
            onClick={() => {
              setEditAttribute("saving_throws");
            }}>
            <b>Saving Throws</b> { obj.saving_throws_string }
          </Grid>
          <Grid
            onClick={() => {
              setEditAttribute("languages");
            }}>
            <b>Languages</b> { obj.languages }
          </Grid>
        </Grid>
      );
    }
  }

  const renderSpeeds = () => {
    const return_me: any[] = [];
    return_me.push(renderSpeed(obj.speed.walk, "walk", "Walk (in feet)"));
    return_me.push(renderSpeed(obj.speed.swim, "swim", "Swim (in feet)"));
    return_me.push(renderSpeed(obj.speed.climb, "climb", "Climb (in feet)"));
    return_me.push(renderSpeed(obj.speed.fly, "fly", "Fly (in feet)"));
    return_me.push(renderSpeed(obj.speed.burrow, "burrow", "Burrow (in feet)"));
    return_me.push(  
      <Grid
        key="hover"
        size={{
          xs: 4
        }}>
        <ToggleButtonBox
          name="Hover"
          value={ obj.speed.hover === 1 }
          disabled={disabled}
          onToggle={() => {
            const new_obj = obj;
            if (obj.speed.hover === 1) {
              new_obj.speed.hover = 0;
            } else {
              new_obj.speed.hover = 1;
            }
            onChange(new_obj);
            setPing(!ping);
          }}
        />
      </Grid>
    );
    return return_me;
  }

  const renderSpeed = (speed: number, name: string, display: string) => {
    return (
      <Grid
        key={name}
        size={{
          xs: 4
        }}>
        <StringBox
          name={ display }
          value={ speed > 0 ? `${speed}` : "" }
          type="number"
          disabled={disabled}
          onBlur={(changed: string) => {
            const new_obj = obj;
            if (changed === "") {
              new_obj.speed[name] = 0;
            } else {
              new_obj.speed[name] = +changed;
            }
            onChange(new_obj);
            setPing(!ping);
          }}
        />
      </Grid>
    );
  }

  const renderSenses = () => {
    const return_me: any[] = [];
    for (const sense of senses) {
      return_me.push(renderSense(sense.name));
    }
    return_me.push(renderPassive(obj.passives.passive_perception, "passive_perception", "Passive Perception"));
    return_me.push(renderPassive(obj.passives.passive_insight, "passive_insight", "Passive Insight"));
    return_me.push(renderPassive(obj.passives.passive_investigation, "passive_investigation", "Passive Investigation"));
    return return_me;
  }

  const renderSense = (name: string) => {
    const range = obj.senses[name];
    return (
      <Grid
        key={name}
        size={{
          xs: 4
        }}>
        <StringBox
          name={ `${name} (in feet)` }
          value={ range ? `${range}` : "" }
          type="number"
          disabled={disabled}
          onBlur={(changed: string) => {
            const new_obj = obj;
            if (changed === "" || changed === "0") {
              delete new_obj.senses[name];
            } else {
              new_obj.senses[name] = +changed;
            }
            onChange(new_obj);
            setPing(!ping);
          }}
        />
      </Grid>
    );
  }

  const renderPassive = (passive: UpgradableNumber, name: string, display: string) => {
    return (
      <Grid
        key={name}
        size={{
          xs: 4
        }}>
        <UpgradableNumberBox
          name={ display }
          slot_level={slot_level}
          value={ passive } 
          disabled={disabled}
          onChange={(changed: UpgradableNumber) => {
            const new_obj = obj;
            ((new_obj.passives as any)[name] as UpgradableNumber).copy(changed);
            onChange(new_obj);
            setPing(!ping);
          }}
        />
      </Grid>
    );
  }
  
  const renderSkill = (skill: Skill) => {
    let upg: { _id: string, name: string, value: UpgradableNumber } | null = null;
    const upg_finder = obj.skill_proficiencies_upgradable.filter(o => o._id === skill._id || o.name === skill.name);
    if (upg_finder.length === 1) {
      upg = upg_finder[0];
    }
    return (
      <Grid
        key={skill._id}
        container
        spacing={0}
        direction="row"
        size={{
          xs: 4
        }}>
        <UpgradableNumberBox
          name={ skill.name }
          slot_level={slot_level}
          value={ upg ? upg.value : new UpgradableNumber() } 
          disabled={disabled}
          onChange={(changed: UpgradableNumber) => {
            const new_obj = obj;
            const upg_finder2 = obj.skill_proficiencies_upgradable.filter(o => o._id === skill._id || o.name === skill.name);
            if (upg_finder2.length === 1) {
              const upg2 = upg_finder2[0];
              upg2._id = skill._id;
              upg2.name = skill.name;
              upg2.value.copy(changed);
            } else {
              new_obj.skill_proficiencies_upgradable.push({ _id: skill._id, name: skill.name, value: changed });
            }
            onChange(new_obj);
            setPing(!ping);
          }}
        />
      </Grid>
    );
  }
  
  const renderTool = (tool: Tool, key: number) => {
    return (
      <Grid
        key={key}
        container
        spacing={0}
        direction="row"
        size={{
          xs: 4
        }}>
        {/* <StringBox
          name={ tool.name }
          value={ obj.tool_proficiencies.includes(tool.name) ? `${obj.tool_proficiencies[tool.name]}` : "" }
          type="number"
          disabled={disabled}
          onBlur={(changed: string) => {
            const new_obj = obj;
            if (changed === "") {
              delete new_obj.tool_proficiencies[tool.name];
            } else {
              new_obj.tool_proficiencies[tool.name] = +changed;
            }
            onChange(new_obj);
            setPing(!ping);
          }}
        /> */}
      </Grid>
    );
  }
  
  const renderCondition = (condition: Condition, key: number) => {
    return (
      <Grid
        key={key}
        container
        spacing={0}
        direction="row"
        size={{
          xs: 4
        }}>
        <ToggleButtonBox
          name={ condition.name }
          value={ obj.condition_immunities.includes(condition.name) }
          disabled={disabled}
          onToggle={() => {
            const new_obj = obj;
            if (obj.condition_immunities.includes(condition.name)) {
              new_obj.condition_immunities = obj.condition_immunities.filter(o => o !== condition.name);
            } else {
              new_obj.condition_immunities.push(condition.name);
            }
            onChange(new_obj);
            setPing(!ping);
          }}
        />
      </Grid>
    );
  }
  
  const renderDamageType = (damage_type: string, key: number, multiplier: number) => {
    return (
      <Grid
        key={key}
        container
        spacing={0}
        direction="row"
        size={{
          xs: 4
        }}>
        <ToggleButtonBox
          name={ damage_type }
          value={ obj.damage_multipliers.filter(o => o.multiplier === multiplier && o.the_damage_types.includes(damage_type)).length > 0 }
          disabled={disabled}
          onToggle={() => {
            const new_obj = obj;
            if (new_obj.damage_multipliers.filter(o => o.multiplier === multiplier && o.the_damage_types.includes(damage_type)).length > 0) {
              new_obj.damage_multipliers = new_obj.damage_multipliers.filter(o => o.multiplier !== multiplier || !o.the_damage_types.includes(damage_type));
            } else {
              const damage_multiplier = new DamageMultiplier();
              damage_multiplier.multiplier = multiplier;
              damage_multiplier.the_damage_types.push(damage_type);
              new_obj.damage_multipliers.push(damage_multiplier);
            }
            onChange(new_obj);
            setPing(!ping);
          }}
        />
      </Grid>
    );
  }

  if (!fetched || loading) {
    return (<LoadingBox />);
  } else { 
    return (
      <Grid container spacing={0} direction="column">
        <Grid>
          <TemplateBox
            obj={obj}
            type="SummonStatBlock"
            useTemplate={(template: TemplateBase) => {
              const new_obj = obj;
              // debugger;
              new_obj.copyTemplate(template, api.all_floating_features);
              onChange(new_obj);
              setReloading(true);
            }}
            useCreature={(creature_id: string) => {
              api.getFullObject("creature", creature_id).then(res => {
                if (res && res instanceof Creature) {
                  copyCreature(res).then((new_obj: Summon) => {
                    onChange(new_obj);
                    setReloading(true);
                  });
                }
              });
            }}
          />
        </Grid>
        <Grid>
          <CenteredMenu
            options={["Home","Attributes","Ability Scores","Actions","Legendary Actions", "Special Abilities"]}
            selected={mode}
            onSelect={setMode}
          />
        </Grid>
        <Grid
          style={{ 
            overflowY: "scroll", 
            overflowX: "hidden" 
          }}>
            { !reloading && render_tab() }
        </Grid>
      </Grid>
    ); 
  }
}
